/**
 * Voice AI — Self Healing Tab (Parent Orchestrator)
 * Sub-tabbed page within Settings. Manages routing between:
 *   Analysis — Conversational feed with analysis cards + chat
 *   Logs     — Timestamped action history with expandable details
 *   Proposals — Tickets from Claude with Execute/Deny/Edit workflow
 *   Settings — Auto-fix toggles, test mode, threshold slider
 */
import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { liveCallsService } from '../../services/live-calls-service.js';

// Sub-components (lazy-loaded on first render)
import './self-healing-monitor.js';
import './self-healing-logs.js';
import './self-healing-proposals.js';
import './self-healing-config.js';

const SUB_TABS = [
  { key: 'analysis',  label: 'Analysis',  icon: 'fa-brain-circuit' },
  { key: 'logs',      label: 'Logs',      icon: 'fa-clipboard-list' },
  { key: 'proposals', label: 'Proposals', icon: 'fa-lightbulb' },
  { key: 'settings',  label: 'Settings',  icon: 'fa-sliders' },
];

export class SelfHealingSettings extends LitElement {
  static properties = {
    _activeSubTab: { state: true },
    _connected: { state: true },
    _serviceStatus: { state: true },
    _pendingProposals: { state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    settingsStyles,
    css`
      /* ─── Info Header ─── */
      .info-card {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-lg);
        padding: var(--space-4) var(--space-5);
        margin-bottom: var(--space-4);
      }
      .info-card h4 {
        font-family: var(--font-display);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
        margin: 0 0 var(--space-2) 0;
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      .info-card p {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-secondary);
        margin: 0;
        line-height: 1.6;
      }
      .info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        gap: var(--space-4);
        margin-top: var(--space-3);
      }
      .info-stat {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .info-stat .label {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }
      .info-stat .value {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
      }
      .info-stat .value.running { color: var(--color-success-text); }
      .info-stat .value.stopped { color: var(--color-error); }

      /* ─── Sub-Tab Bar ─── */
      .sub-tab-bar {
        display: flex;
        gap: 2px;
        background: var(--color-bg-tertiary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-lg);
        padding: 3px;
        margin-bottom: var(--space-4);
      }

      .sub-tab {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        border: none;
        background: transparent;
        color: var(--color-text-secondary);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
        position: relative;
      }
      .sub-tab:hover {
        color: var(--color-text-primary);
        background: var(--color-bg-secondary);
      }
      .sub-tab.active {
        background: var(--color-bg-secondary);
        color: var(--color-text-primary);
        font-weight: var(--font-semibold);
        box-shadow: 0 1px 3px rgba(0,0,0,0.08);
      }

      .sub-tab i {
        font-size: 0.8125rem;
      }

      .badge {
        position: absolute;
        top: 2px;
        right: 6px;
        min-width: 16px;
        height: 16px;
        border-radius: 8px;
        background: #f85149;
        color: white;
        font-family: var(--font-mono);
        font-size: 0.5625rem;
        font-weight: var(--font-bold);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
      }

      /* ─── Content Area ─── */
      .sub-tab-content {
        min-height: 400px;
      }
    `
  ];

  constructor() {
    super();
    this._activeSubTab = 'analysis';
    this._connected = false;
    this._serviceStatus = null;
    this._pendingProposals = 0;
  }

  connectedCallback() {
    super.connectedCallback();

    this._connected = liveCallsService.isConnected();
    this._unsubConn = liveCallsService.onChange((_calls, connected) => {
      this._connected = connected;
    });

    // Listen for new proposals to update badge
    this._unsubProposal = liveCallsService.onMonitorProposal(() => {
      this._pendingProposals++;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._unsubConn) this._unsubConn();
    if (this._unsubProposal) this._unsubProposal();
  }

  _setSubTab(key) {
    this._activeSubTab = key;
    // Clear badge when viewing proposals
    if (key === 'proposals') {
      this._pendingProposals = 0;
    }
  }

  render() {
    return html`
      ${iconStyles}

      <div class="info-card">
        <h4>
          <i class="fa-sharp fa-light fa-heart-pulse"></i>
          Self-Healing Monitor
        </h4>
        <p>
          Watches orchestrator logs in real-time. When calls complete, Claude Opus analyzes
          them for quality issues and auto-tunes pipeline settings. Session-persistent memory
          tracks patterns across analyses.
        </p>
        <div class="info-grid">
          <div class="info-stat">
            <span class="label">WebSocket</span>
            <span class="value ${this._connected ? 'running' : 'stopped'}">
              ${this._connected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      </div>

      <div class="sub-tab-bar">
        ${SUB_TABS.map(tab => html`
          <button
            class="sub-tab ${this._activeSubTab === tab.key ? 'active' : ''}"
            @click=${() => this._setSubTab(tab.key)}
          >
            <i class="fa-sharp fa-light ${tab.icon}"></i>
            ${tab.label}
            ${tab.key === 'proposals' && this._pendingProposals > 0 ? html`
              <span class="badge">${this._pendingProposals}</span>
            ` : ''}
          </button>
        `)}
      </div>

      <div class="sub-tab-content">
        ${this._activeSubTab === 'analysis' ? html`<self-healing-monitor></self-healing-monitor>` : ''}
        ${this._activeSubTab === 'logs' ? html`<self-healing-logs></self-healing-logs>` : ''}
        ${this._activeSubTab === 'proposals' ? html`<self-healing-proposals></self-healing-proposals>` : ''}
        ${this._activeSubTab === 'settings' ? html`<self-healing-config></self-healing-config>` : ''}
      </div>
    `;
  }
}

customElements.define('self-healing-settings', SelfHealingSettings);
